import { stripLegacyPrefix } from '@/components/actions/constants/actionApplicability';
import { Seed, SeedStatus } from '@/types';

// These sets mirror the backend per-asset scan-status gate as a DENYLIST
// (job.Service.Put nonScannableStatus: POST /job returns HTTP 400 for
// F/FR/D/P) plus the 2026-07-08 pending-whois carve-out: an
// explicitly-requested "Refresh Domain Data" (whois) IS allowed on Pending
// targets, but scans are not; F/FR/D block everything including whois.
// PendingLow ('PL') is a UI-only legacy alias for Pending and is treated as P.
// 'U' (Unavailable, shown as "Dormant") is a known status that deliberately
// stays actionable: scanning/refreshing is how a user re-checks resolution,
// and the backend nonScannableStatus gate doesn't block it. Truly unknown
// statuses still FAIL OPEN — the UI gating is UX-only and enforcement is
// server-side, so anything the backend doesn't explicitly block stays
// actionable here.

/** Statuses that block a security scan (backend returns HTTP 400). */
const NON_SCANNABLE_STATUSES: ReadonlySet<string> = new Set([
  SeedStatus.Freeze,
  SeedStatus.Reject,
  SeedStatus.Delete,
  SeedStatus.Pending,
  SeedStatus.PendingLow,
]);

/** Statuses that block an explicitly-requested WHOIS refresh (Pending allowed). */
const NON_REFRESHABLE_STATUSES: ReadonlySet<string> = new Set([
  SeedStatus.Freeze,
  SeedStatus.Reject,
  SeedStatus.Delete,
]);

/**
 * Normalize a raw status the same way the gating classifier and the status
 * badge do (shared `stripLegacyPrefix`): the legacy `domain#`/`ip#` composite
 * prefix (client-side statistics keys) is stripped before the denylist check,
 * so display and gating can never disagree on what a raw status string means.
 */
const normalizeStatus = (status: string): string =>
  stripLegacyPrefix(status) ?? '';

/** True unless the seed's status blocks scans (F, FR, D, P, PL). */
export function isSeedScannable(seed: Pick<Seed, 'status'>): boolean {
  return !NON_SCANNABLE_STATUSES.has(normalizeStatus(seed.status));
}

/** True unless the seed's status blocks a WHOIS refresh (F, FR, D). */
export function isSeedRefreshable(seed: Pick<Seed, 'status'>): boolean {
  return !NON_REFRESHABLE_STATUSES.has(normalizeStatus(seed.status));
}
